export interface OrgPlanTierLabels {
  PLAN_TIER_STARTER: string
  PLAN_TIER_PRO: string
  PLAN_TIER_BUSINESS: string
  PLAN_TIER_ENTERPRISE: string
}

export interface OrgSubscriptionStatusLabels {
  SUBSCRIPTION_STATUS_ACTIVE: string
  SUBSCRIPTION_STATUS_TRIALING: string
  SUBSCRIPTION_STATUS_PAST_DUE: string
  SUBSCRIPTION_STATUS_CANCELED: string
  SUBSCRIPTION_STATUS_UNPAID: string
  SUBSCRIPTION_STATUS_INCOMPLETE: string
  SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED: string
  SUBSCRIPTION_STATUS_PAUSED: string
}

/**
 * Format a Prisma planTier enum value for display.
 */
export function formatPlanTierLabel(tier: string): string {
  if (tier === 'STARTER') return 'Starter (Free)'
  if (tier === 'PRO') return 'Pro'
  if (tier === 'BUSINESS') return 'Business'
  if (tier === 'ENTERPRISE') return 'Enterprise'
  return tier
}

/** Locale-aware org plan tier label (account-admin billing tab). */
export function formatOrgPlanTierLabel(tier: string, labels: OrgPlanTierLabels): string {
  if (tier === 'STARTER' || tier === 'FREE') return labels.PLAN_TIER_STARTER
  if (tier === 'PRO') return labels.PLAN_TIER_PRO
  if (tier === 'BUSINESS') return labels.PLAN_TIER_BUSINESS
  if (tier === 'ENTERPRISE') return labels.PLAN_TIER_ENTERPRISE
  return tier
}

/** Individual consumer tier from `consumerBilling.planTier` (not org B2B). */
export function formatConsumerPlanLabel(tier: 'FREE' | 'PRO'): string {
  if (tier === 'PRO') return 'Pro (personal)'
  return 'Hobby (Free)'
}

/**
 * Format an ISO date string (or null) as a short locale date.
 */
export function formatPeriodEnd(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Short date for compact badges (pricing, alerts), e.g. "Jun 2, 2026". */
export function formatPeriodEndShort(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Human-friendly subscription status (lowercased Stripe status → display).
 */
export function formatSubscriptionStatus(status: string | null | undefined): string {
  if (!status) return '—'
  const s = status.trim().toLowerCase()
  if (s === 'active') return 'Active'
  if (s === 'trialing') return 'Trialing'
  if (s === 'past_due') return 'Past due'
  if (s === 'canceled') return 'Canceled'
  if (s === 'unpaid') return 'Unpaid'
  if (s === 'incomplete') return 'Incomplete'
  if (s === 'incomplete_expired') return 'Expired'
  if (s === 'paused') return 'Paused'
  return status
}

/** Locale-aware Stripe subscription status (account-admin billing tab). */
export function formatOrgSubscriptionStatus(
  status: string | null | undefined,
  labels: OrgSubscriptionStatusLabels,
): string {
  if (!status) return '—'
  const s = status.trim().toLowerCase()
  if (s === 'active') return labels.SUBSCRIPTION_STATUS_ACTIVE
  if (s === 'trialing') return labels.SUBSCRIPTION_STATUS_TRIALING
  if (s === 'past_due') return labels.SUBSCRIPTION_STATUS_PAST_DUE
  if (s === 'canceled') return labels.SUBSCRIPTION_STATUS_CANCELED
  if (s === 'unpaid') return labels.SUBSCRIPTION_STATUS_UNPAID
  if (s === 'incomplete') return labels.SUBSCRIPTION_STATUS_INCOMPLETE
  if (s === 'incomplete_expired') return labels.SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED
  if (s === 'paused') return labels.SUBSCRIPTION_STATUS_PAUSED
  return status
}

export interface ConsumerSubscriptionStatusOptions {
  cancelAtPeriodEnd?: boolean
  periodEndIso?: string | null
}

/**
 * Consumer Pro row: Stripe stays `active` until period end when user cancels renewal — explain that clearly.
 */
export function formatConsumerSubscriptionStatus(
  status: string | null | undefined,
  options?: ConsumerSubscriptionStatusOptions,
): string {
  if (options?.cancelAtPeriodEnd === true) {
    const end = options.periodEndIso ? formatPeriodEnd(options.periodEndIso) : null
    if (end && end !== '—') {
      return `Cancelled — Pro access until ${end} (resume anytime in the billing portal)`
    }
    return 'Cancelled — Pro access until period end (resume anytime in the billing portal)'
  }
  return formatSubscriptionStatus(status)
}
